import React, { useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Chip,
    Select,
    MenuItem,
    InputLabel,
    FormControl,
    Pagination,
    TableContainer,
    Paper
} from "@mui/material";
import {
    collection,
    getDocs,
    doc,
    setDoc,
    deleteDoc,
    query,
    where,
    Timestamp
} from "firebase/firestore";
import { createUserWithEmailAndPassword, getAuth, onAuthStateChanged, signInWithEmailAndPassword } from "firebase/auth";
import { db } from "../firebase";
import Swal from "sweetalert2";

const colors = {
    darkGreen: '#06362D',
    calPolyGreen: '#155332',
    vanilla: '#DEE2B1',
    gold: '#FFD500',
    goldenrod: '#D1A505'
};

const PAGE_SIZE = 5;
const ROLE_OPTIONS = [
    "Reception",
    "Accountant",
    "Store",
    "General Manager",
    "Housekeeping",
    "Restoadmin",
    "Captain",
    "Manager",
    "Chef",
];

function Userlogins() {
    const auth = getAuth();

    const [userId, setUserId] = useState(null);
    const [propertyId, setPropertyId] = useState(null);

    const [users, setUsers] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [loginemail, setloginemail] = useState("");
    const [loginpass, setloginpass] = useState("");

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);

    const [form, setForm] = useState({
        name: "",
        email: "",
        mobile: "",
        password: "",
        role: "Reception",
        status: "Active",
        propertyId: ""
    });

    /* ================= AUTH ================= */
    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) return;
            setUserId(user.uid);

            const snap = await getDocs(collection(db, "authroles"));
            snap.forEach(d => {
                if (d.id === user.uid) {
                    setPropertyId(d.data().propertyId);
                    setloginemail(d.data().email);
                    setloginpass(d.data().password);
                }
            });
        });
        return () => unsub();
    }, []);

    /* ================= LOAD USERS ================= */
    useEffect(() => {
        if (!propertyId) return;
        loadUsers();
    }, [propertyId]);

    const loadUsers = async () => {
        const q = query(
            collection(db, "authroles"),
            where("propertyId", "==", propertyId)
        );
        const snap = await getDocs(q);
        setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    };

    /* ================= HANDLERS ================= */
    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const saveUser = async () => {
        const userCredential = await createUserWithEmailAndPassword(auth, form.email, form.password);
        const user = userCredential.user;

        await setDoc(doc(db, "authroles", user.uid), {
            uid: user.uid,
            ...form,
            propertyId,
            createdTime: editingId ? form.createdTime : Timestamp.now()
        });

        await auth.signOut();
        await signInWithEmailAndPassword(auth, loginemail, loginpass);

        Swal.fire("Success", editingId ? "User updated" : "User added", "success");

        setForm({
            uid: "",
            name: "",
            email: "",
            mobile: "",
            password: "",
            role: "Reception",
            status: "Active",
            propertyId: ""
        });

        setEditingId(null);
        loadUsers();
    };

    /* ================= FILTER + PAGINATION ================= */
    const filtered = users.filter(u =>
        u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase())
    );

    const paginated = filtered.slice(
        (page - 1) * PAGE_SIZE,
        page * PAGE_SIZE
    );

    return (
        <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: colors.darkGreen, color: colors.vanilla, p: { xs: 2, md: 4 } }}>
            <Box component="main" sx={{ flexGrow: 1, maxWidth: "1400px", margin: "0 auto" }}>
                
                <Typography variant="h5" fontWeight={800} mb={3} sx={{ color: colors.vanilla }}>
                    User Logins
                </Typography>

                {/* ================= FORM ================= */}
                <Card sx={{ mb: 3, borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
                    <CardContent sx={{ p: 3 }}>
                        <Typography fontWeight={700} mb={2} sx={{ color: colors.vanilla }}>
                            {editingId ? "Edit User" : "Add New User"}
                        </Typography>

                        <div className="container-fluid p-0">
                            <div className="row">
                                <div className="col-sm-3 mb-3">
                                    <TextField 
                                        fullWidth 
                                        size="small" 
                                        label="Name" 
                                        name="name" 
                                        value={form.name} 
                                        onChange={handleChange}
                                        InputLabelProps={{ style: { color: colors.vanilla } }}
                                        InputProps={{ style: { color: colors.vanilla } }}
                                        sx={{ 
                                            "& .MuiOutlinedInput-root": { 
                                                borderRadius: "8px",
                                                backgroundColor: colors.darkGreen,
                                                "& fieldset": { borderColor: colors.goldenrod },
                                                "&:hover fieldset": { borderColor: colors.gold },
                                                "&.Mui-focused fieldset": { borderColor: colors.gold }
                                            } 
                                        }}
                                    />
                                </div>
                                <div className="col-sm-3 mb-3">
                                    <TextField 
                                        fullWidth 
                                        size="small" 
                                        label="Email" 
                                        name="email" 
                                        value={form.email} 
                                        onChange={handleChange}
                                        InputLabelProps={{ style: { color: colors.vanilla } }}
                                        InputProps={{ style: { color: colors.vanilla } }}
                                        sx={{ 
                                            "& .MuiOutlinedInput-root": { 
                                                borderRadius: "8px",
                                                backgroundColor: colors.darkGreen,
                                                "& fieldset": { borderColor: colors.goldenrod },
                                                "&:hover fieldset": { borderColor: colors.gold },
                                                "&.Mui-focused fieldset": { borderColor: colors.gold }
                                            } 
                                        }}
                                    />
                                </div>
                                <div className="col-sm-3 mb-3">
                                    <TextField 
                                        fullWidth 
                                        size="small" 
                                        label="Mobile" 
                                        name="mobile" 
                                        value={form.mobile} 
                                        onChange={handleChange}
                                        InputLabelProps={{ style: { color: colors.vanilla } }}
                                        InputProps={{ style: { color: colors.vanilla } }}
                                        sx={{ 
                                            "& .MuiOutlinedInput-root": { 
                                                borderRadius: "8px",
                                                backgroundColor: colors.darkGreen,
                                                "& fieldset": { borderColor: colors.goldenrod },
                                                "&:hover fieldset": { borderColor: colors.gold },
                                                "&.Mui-focused fieldset": { borderColor: colors.gold }
                                            } 
                                        }}
                                    />
                                </div>
                                <div className="col-sm-3 mb-3">
                                    <TextField 
                                        fullWidth 
                                        size="small" 
                                        label="Password" 
                                        name="password" 
                                        value={form.password} 
                                        onChange={handleChange}
                                        InputLabelProps={{ style: { color: colors.vanilla } }}
                                        InputProps={{ style: { color: colors.vanilla } }}
                                        sx={{ 
                                            "& .MuiOutlinedInput-root": { 
                                                borderRadius: "8px",
                                                backgroundColor: colors.darkGreen,
                                                "& fieldset": { borderColor: colors.goldenrod },
                                                "&:hover fieldset": { borderColor: colors.gold },
                                                "&.Mui-focused fieldset": { borderColor: colors.gold }
                                            } 
                                        }}
                                    />
                                </div>
                                <div className="col-sm-3 mb-3">
                                    <FormControl fullWidth size="small">
                                        <InputLabel sx={{ color: colors.vanilla }}>Role</InputLabel>
                                        <Select 
                                            name="role" 
                                            value={form.role} 
                                            onChange={handleChange} 
                                            label="Role"
                                            sx={{ 
                                                color: colors.vanilla, 
                                                backgroundColor: colors.darkGreen,
                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.goldenrod }, 
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.gold }, 
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.gold },
                                                '& .MuiSvgIcon-root': { color: colors.vanilla }
                                            }}
                                        >
                                            {ROLE_OPTIONS.map(r => (
                                                <MenuItem key={r} value={r}>{r}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </div>
                            </div>
                        </div>

                        <Button 
                            variant="contained" 
                            sx={{ 
                                mt: 2, 
                                bgcolor: colors.goldenrod, 
                                color: colors.darkGreen, 
                                fontWeight: 700, 
                                textTransform: "none", 
                                borderRadius: "8px",
                                '&:hover': { bgcolor: colors.gold } 
                            }} 
                            onClick={saveUser}
                        >
                            {editingId ? "Update User" : "Add User"}
                        </Button>
                    </CardContent>
                </Card>

                {/* ================= SEARCH ================= */}
                <TextField
                    fullWidth
                    size="small"
                    label="Search by name or email"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    InputLabelProps={{ style: { color: colors.vanilla } }}
                    InputProps={{ style: { color: colors.vanilla } }}
                    sx={{ 
                        mb: 3,
                        "& .MuiOutlinedInput-root": { 
                            borderRadius: "8px",
                            backgroundColor: colors.calPolyGreen,
                            "& fieldset": { borderColor: colors.goldenrod },
                            "&:hover fieldset": { borderColor: colors.gold },
                            "&.Mui-focused fieldset": { borderColor: colors.gold }
                        } 
                    }}
                />

                {/* ================= TABLE ================= */}
                <Card sx={{ borderRadius: 3, border: `1px solid ${colors.goldenrod}`, bgcolor: colors.calPolyGreen, color: colors.vanilla, boxShadow: "0 4px 20px 0 rgba(0,0,0,0.3)" }}>
                    <CardContent sx={{ p: 3 }}>
                        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: "8px", border: `1px solid ${colors.goldenrod}`, backgroundColor: "transparent" }}>
                            <Table size="small">
                                <TableHead sx={{ bgcolor: colors.darkGreen }}>
                                    <TableRow>
                                        <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Sr No</TableCell>
                                        <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Name</TableCell>
                                        <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Mobile</TableCell>
                                        <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Email</TableCell>
                                        <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Role</TableCell>
                                        <TableCell sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Status</TableCell>
                                        <TableCell align="center" sx={{ color: colors.vanilla, fontWeight: 700, fontSize: '0.75rem' }}>Actions</TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {paginated.length === 0 && (
                                        <TableRow>
                                            <TableCell colSpan={7} align="center" sx={{ py: 4, color: colors.vanilla, opacity: 0.7 }}>
                                                No Records Found
                                            </TableCell>
                                        </TableRow>
                                    )}

                                    {paginated.map((u, index) => (
                                        <TableRow key={u.uid} hover sx={{ '&:hover': { bgcolor: 'rgba(255,255,255,0.05)' } }}>
                                            <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                                                {(page - 1) * PAGE_SIZE + index + 1}
                                            </TableCell>
                                            <TableCell sx={{ color: colors.gold, fontWeight: 600, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{u.name}</TableCell>
                                            <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{u.mobile}</TableCell>
                                            <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{u.email}</TableCell>
                                            <TableCell sx={{ color: colors.vanilla, borderBottom: `1px solid rgba(222,226,177,0.1)` }}>{u.role}</TableCell>
                                            <TableCell sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                                                <Chip
                                                    label={u.status}
                                                    color={u.status === "Active" ? "success" : "default"}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center" sx={{ borderBottom: `1px solid rgba(222,226,177,0.1)` }}>
                                                {/* Actions */}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        <Box display="flex" justifyContent="center" mt={3}>
                            <Pagination
                                count={Math.ceil(filtered.length / PAGE_SIZE)}
                                page={page}
                                onChange={(e, v) => setPage(v)}
                                color="primary"
                                size="small"
                            />
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}

export default Userlogins;